// src/components/nav/PharmacyDesktopHeader.jsx //
import React from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { MessageSquareWarning, Monitor, Moon, Sun, UserRound } from "lucide-react";
import { logout } from "@/lib/auth/logout";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import AfrimedSearchBar from "@/components/ui/AfrimedSearchBar.jsx";
import NavAlertBadge from "@/components/nav/NavAlertBadge.jsx";
import useUnreadMessages from "@/hooks/useUnreadMessages.js";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import AfriMedDialog from "@/sot/primitives/AfriMedDialog.jsx";
import {
  groupAlertsForAttention,
  hydratePharmacyAlert,
} from "@/utils/alertModel.js";
import {
  PHARMACY_PROFILE_CHANGED_EVENT,
  PHARMACY_PROFILE_STORAGE_KEY,
  markPharmacyNotificationsRead,
  readPharmacyProfile,
} from "@/services/pharmacyLaneService.js";
import {
  PHARMACY_MEMBER_ACCOUNT_UPDATED_EVENT,
  getPharmacyMemberAccount,
  getPharmacyMemberPhotoUrl,
  updatePharmacyMemberThemePreference,
} from "@/services/pharmacyMemberAccountService.js";
import { submitContextFeedback } from "@/services/contextFeedbackService.js";

const BRAND_LOGO = "/assets/images/afrimed_logo.svg";
const FALLBACK_AVATAR = "/icons/circular-avatar-placeholder.svg";
const ICON_SEARCH = "/icons/pharmacy/search.svg";
const ICON_BELL = "/icons/pharmacy/notifications-bell.svg";
const ICON_DROPDOWN = "/icons/pharmacy/drop-down-chevron.svg";
const ICON_CLOSE = "/icons/doctor/close.svg";
const ICON_PRESCRIPTIONS = "/icons/pharmacy/received-prescriptions.svg";
const ICON_SETTINGS = "/icons/pharmacy/settings.svg";
const ICON_HELP = "/icons/pharmacy/help.svg";
const ICON_SIGN_OUT = "/icons/appointments/dashboard/log-out.svg";
const ICON_DASHBOARD = "/icons/pharmacy/dashboard.svg";
const ICON_INVENTORY = "/icons/pharmacy/inventory.svg";
const ICON_PAYMENTS = "/icons/pharmacy/payments.svg";
const ICON_LOG_OUT_CRITICAL = "/icons/appointments/dashboard/log-out-critical.svg";
const WELLNESS_BADGE_BG = "bg-[#00BDC4]";
const WELLNESS_BADGE_SOFT = "bg-[rgba(0,189,196,0.12)]";
const WELLNESS_BADGE_TEXT = "text-[#008A8F]";

function DashboardNavIcon({ active = false }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={[
        "h-5 w-5 shrink-0",
        active
          ? "text-[rgb(var(--clr-on-surface))]"
          : "text-[rgb(var(--clr-on-surface-variant))] group-hover:text-[rgb(var(--clr-on-surface))]",
      ].join(" ")}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.30775 20.5C4.80258 20.5 4.375 20.325 4.025 19.975C3.675 19.625 3.5 19.1974 3.5 18.6923V5.30775C3.5 4.80258 3.675 4.375 4.025 4.025C4.375 3.675 4.80258 3.5 5.30775 3.5H9.25C10.3546 3.5 11.25 4.39543 11.25 5.5V18.5C11.25 19.6046 10.3546 20.5 9.25 20.5H5.30775ZM14.75 20.5C13.6454 20.5 12.75 19.6046 12.75 18.5V14C12.75 12.8954 13.6454 12 14.75 12H18.5C19.6046 12 20.5 12.8954 20.5 14V18.6923C20.5 19.1974 20.325 19.625 19.975 19.975C19.625 20.325 19.1974 20.5 18.6923 20.5H14.75ZM14.75 10.5C13.6454 10.5 12.75 9.60457 12.75 8.5V5.5C12.75 4.39543 13.6454 3.5 14.75 3.5H18.6923C19.1974 3.5 19.625 3.675 19.975 4.025C20.325 4.375 20.5 4.80258 20.5 5.30775V8.5C20.5 9.60457 19.6046 10.5 18.5 10.5H14.75Z"
        fill="currentColor"
      />
    </svg>
  );
}

function MessagesNavIcon({ active = false }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={[
        "h-5 w-5 shrink-0",
        active
          ? "text-[rgb(var(--clr-on-surface))]"
          : "text-[rgb(var(--clr-on-surface-variant))] group-hover:text-[rgb(var(--clr-on-surface))]",
      ].join(" ")}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.30775 19.5C5.80258 19.5 5.375 19.325 5.025 18.975C4.675 18.625 4.5 18.1974 4.5 17.6923V6.30775C4.5 5.80258 4.675 5.375 5.025 5.025C5.375 4.675 5.80258 4.5 6.30775 4.5H17.6923C18.1974 4.5 18.625 4.675 18.975 5.025C19.325 5.375 19.5 5.80258 19.5 6.30775V13.9231C19.5 14.4283 19.325 14.8558 18.975 15.2058C18.625 15.5558 18.1974 15.7308 17.6923 15.7308H9.80775L5.65388 19.1539C5.36713 19.3886 5.06608 19.4359 4.75073 19.2958C4.43538 19.1558 4.2777 18.9072 4.2777 18.55V17.6923C4.2777 17.1974 4.4527 16.7738 4.8027 16.4215C5.1527 16.0692 5.57963 15.8921 6.0835 15.8902L6.30775 15.8892V19.5Z"
        fill="currentColor"
      />
      <path
        d="M8 9H16"
        stroke="rgb(var(--clr-surface))"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <path
        d="M8 12.5H13.5"
        stroke="rgb(var(--clr-surface))"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function AccountNavIcon() {
  return <UserRound className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant))]" aria-hidden />;
}

function FeedbackNavIcon() {
  return (
    <MessageSquareWarning
      className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant))]"
      aria-hidden
    />
  );
}

function clsChip(active) {
  return [
    "h-9 px-1.5 lg:h-10 lg:px-2 flex items-center justify-center text-[13px] lg:text-body-medium border-b-2 transition",
    active
      ? "text-[rgb(var(--clr-on-surface))] border-[rgba(var(--clr-on-surface),0.88)]"
      : "border-transparent text-[rgb(var(--clr-on-surface-variant))] hover:text-[rgb(var(--clr-on-surface))]",
  ].join(" ");
}

function MenuItem({ label, icon, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 px-4 py-3 text-left text-body-medium text-[rgb(var(--clr-on-surface))] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
    >
      {typeof icon === "function" ? (
        icon()
      ) : (
        <img src={icon} alt="" className="h-5 w-5" draggable="false" />
      )}
      <span className="flex-1">{label}</span>
    </button>
  );
}

function ThemeOptionButton({ active, label, icon: Icon, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex w-full items-center gap-3 rounded-[12px] px-3 py-2 text-left text-body-medium transition",
        active
          ? "bg-[rgba(var(--afrimed-primary),0.10)] text-[rgb(var(--clr-on-surface))]"
          : "text-[rgb(var(--clr-on-surface))] hover:bg-[rgba(var(--clr-on-surface),0.06)]",
      ].join(" ")}
    >
      <Icon className="h-[18px] w-[18px] text-[rgb(var(--clr-on-surface-variant))]" aria-hidden />
      <span className="flex-1">{label}</span>
      <span
        className={[
          "h-2.5 w-2.5 rounded-full border",
          active
            ? "border-[rgb(var(--afrimed-primary))] bg-[rgb(var(--afrimed-primary))]"
            : "border-[rgba(var(--clr-outline),0.9)] bg-transparent",
        ].join(" ")}
      />
    </button>
  );
}

const THEME_OPTIONS = [
  { value: "dark", label: "Dark", icon: Moon },
  { value: "light", label: "Light", icon: Sun },
  { value: "system", label: "System", icon: Monitor },
];

function MobileNavButton({ label, icon, active = false, badgeCount = 0, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "group flex w-full items-center gap-3 rounded-[12px] px-4 py-3 text-left transition",
        active
          ? "bg-[rgba(var(--afrimed-primary),0.10)] text-[rgb(var(--clr-on-surface))]"
          : "text-[rgb(var(--clr-on-surface))] hover:bg-[rgba(var(--clr-on-surface),0.06)]",
      ].join(" ")}
    >
      {typeof icon === "function" ? (
        icon({ active })
      ) : (
        <img src={icon} alt="" className="h-5 w-5 shrink-0" draggable="false" />
      )}
      <span className="flex-1 text-body-large">{label}</span>
      {Number(badgeCount || 0) > 0 ? (
        <NavAlertBadge count={badgeCount} className="shrink-0" />
      ) : null}
    </button>
  );
}

function HamburgerIcon() {
  return (
    <span className="flex h-4 w-5 flex-col items-center justify-between" aria-hidden="true">
      <span className="block h-0.5 w-5 rounded-full bg-[rgb(var(--clr-on-surface))]" />
      <span className="block h-0.5 w-5 rounded-full bg-[rgb(var(--clr-on-surface))]" />
      <span className="block h-0.5 w-5 rounded-full bg-[rgb(var(--clr-on-surface))]" />
    </span>
  );
}

function firstText(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

function resolvePharmacyAvatarSource(auth = {}) {
  const profile = auth?.profile || {};
  const userMeta = auth?.user?.user_metadata || {};

  return firstText(
    profile?.pharmacy_avatar_url,
    profile?.pharmacy_profile_image_url,
    profile?.pharmacy_profile_photo_url,
    profile?.pharmacy_member_avatar_url,
    userMeta?.pharmacy_avatar_url,
    userMeta?.pharmacy_profile_image_url,
    userMeta?.pharmacy_member_avatar_url,
  );
}

function resolvePharmacySearchTarget(pathname = "") {
  const path = String(pathname || "").trim();
  if (
    path === "/pharmacy/messages" ||
    path.startsWith("/pharmacy/messages/") ||
    path === "/pharmacy/inventory" ||
    path === "/pharmacy/prescriptions" ||
    path === "/pharmacy/payments"
  ) {
    return path;
  }
  return "/pharmacy/prescriptions";
}

export default function PharmacyDesktopHeader({ section = "dashboard", notifications = [] }) {
  const nav = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = React.useState(false);
  const accountMenuRef = React.useRef(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [notificationsOpen, setNotificationsOpen] = React.useState(false);
  const [loggingOut, setLoggingOut] = React.useState(false);
  const [logoutOpen, setLogoutOpen] = React.useState(false);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [feedbackNote, setFeedbackNote] = React.useState("");
  const [feedbackBusy, setFeedbackBusy] = React.useState(false);
  const [feedbackError, setFeedbackError] = React.useState("");
  const [feedbackSent, setFeedbackSent] = React.useState(false);
  const [avatarFailed, setAvatarFailed] = React.useState(false);
  const [pharmacyMemberAccount, setPharmacyMemberAccount] = React.useState(null);
  const [pharmacyMemberPhotoUrl, setPharmacyMemberPhotoUrl] = React.useState("");
  const [searchValue, setSearchValue] = React.useState("");
  const messagesRoute = "/pharmacy/messages";
  const { unreadCount: unreadMessages } = useUnreadMessages(auth?.user?.id);
  const explicitPharmacyAvatarSrc = React.useMemo(
    () => firstText(pharmacyMemberPhotoUrl, resolvePharmacyAvatarSource(auth)),
    [auth, pharmacyMemberPhotoUrl],
  );
  const userId = String(auth?.user?.id || "").trim();
  const userEmail = String(auth?.user?.email || "").trim().toLowerCase();
  const [pharmacyProfile, setPharmacyProfile] = React.useState(() => readPharmacyProfile() || {});

  const tabs = [
    {
      key: "dashboard",
      to: "/pharmacy/dashboard",
      label: t("pharmacy.nav.dashboard", "Dashboard"),
      icon: ({ active }) => <DashboardNavIcon active={active} />,
      match: (path) => path === "/pharmacy/dashboard" || path.startsWith("/pharmacy/dashboard/"),
    },
    {
      key: "inventory",
      to: "/pharmacy/inventory",
      label: t("pharmacy.nav.inventory", "Inventory"),
      icon: ICON_INVENTORY,
      match: (path) => path === "/pharmacy/inventory" || path.startsWith("/pharmacy/inventory/"),
    },
    {
      key: "payments",
      to: "/pharmacy/payments",
      label: t("pharmacy.nav.payments", "Payments"),
      icon: ICON_PAYMENTS,
      match: (path) => path === "/pharmacy/payments" || path.startsWith("/pharmacy/payments/"),
    },
    {
      key: "messages",
      to: messagesRoute,
      label: t("pharmacy.nav.messages", "Messages"),
      icon: ({ active }) => <MessagesNavIcon active={active} />,
      badgeCount: unreadMessages,
      match: (path) => path === "/pharmacy/messages" || path.startsWith("/pharmacy/messages/"),
    },
  ];
  const mobileNavItems = [
    ...tabs,
    { key: "prescriptions", to: "/pharmacy/prescriptions", label: t("pharmacy.nav.prescriptions", "Prescriptions"), icon: ICON_PRESCRIPTIONS },
    { key: "settings", to: "/pharmacy/settings", label: t("pharmacy.nav.settings", "Settings"), icon: ICON_SETTINGS },
    { key: "help", to: "/pharmacy/help", label: t("pharmacy.nav.help", "Help"), icon: ICON_HELP },
  ];

  React.useEffect(() => {
    setOpen(false);
    setMobileOpen(false);
    setNotificationsOpen(false);
  }, [section]);

  React.useEffect(() => {
    setAvatarFailed(false);
  }, [explicitPharmacyAvatarSrc]);

  React.useEffect(() => {
    function refreshProfile() {
      setPharmacyProfile(readPharmacyProfile() || {});
    }

    function handleStorage(event) {
      if (!event?.key || event.key === PHARMACY_PROFILE_STORAGE_KEY) {
        refreshProfile();
      }
    }

    refreshProfile();
    window.addEventListener(PHARMACY_PROFILE_CHANGED_EVENT, refreshProfile);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(PHARMACY_PROFILE_CHANGED_EVENT, refreshProfile);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  React.useEffect(() => {
    let alive = true;

    async function loadPharmacyMemberAccount() {
      if (!userId) {
        if (!alive) return;
        setPharmacyMemberAccount(null);
        setPharmacyMemberPhotoUrl("");
        return;
      }

      try {
        const nextAccount = await getPharmacyMemberAccount({
          userId,
          email: userEmail,
          fullName:
            firstText(auth?.profile?.full_name, auth?.profile?.display_name),
        });
        if (!alive) return;
        setPharmacyMemberAccount(nextAccount);
        if (firstText(nextAccount?.profile_photo_path)) {
          const signedUrl = await getPharmacyMemberPhotoUrl(nextAccount.profile_photo_path);
          if (!alive) return;
          setPharmacyMemberPhotoUrl(signedUrl);
        } else {
          setPharmacyMemberPhotoUrl("");
        }
      } catch {
        if (!alive) return;
        setPharmacyMemberAccount(null);
        setPharmacyMemberPhotoUrl("");
      }
    }

    loadPharmacyMemberAccount();

    function handleProfileUpdated() {
      loadPharmacyMemberAccount();
    }

    window.addEventListener(PHARMACY_MEMBER_ACCOUNT_UPDATED_EVENT, handleProfileUpdated);
    return () => {
      alive = false;
      window.removeEventListener(PHARMACY_MEMBER_ACCOUNT_UPDATED_EVENT, handleProfileUpdated);
    };
  }, [auth?.profile?.display_name, auth?.profile?.full_name, userEmail, userId]);

  React.useEffect(() => {
    const persistedTheme = String(pharmacyMemberAccount?.theme_preference || "")
      .trim()
      .toLowerCase();
    if (!persistedTheme || persistedTheme === theme) return;
    setTheme(persistedTheme);
  }, [pharmacyMemberAccount?.theme_preference, setTheme, theme]);

  const handleSelectTheme = React.useCallback(async (nextTheme) => {
    setTheme(nextTheme);
    if (!userId) return;
    try {
      await updatePharmacyMemberThemePreference({ userId, themePreference: nextTheme });
    } catch {
      // The current workspace theme remains applied; persistence can retry on the next selection.
    }
  }, [setTheme, userId]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSearchValue(String(params.get("q") || "").trim());
  }, [location.search]);

  React.useEffect(() => {
    if (!mobileOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  React.useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      if (!accountMenuRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  React.useEffect(() => {
    if (!notificationsOpen) return undefined;
    if (typeof window !== "undefined" && window.innerWidth >= 768) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [notificationsOpen]);

  async function handleLogout() {
    if (loggingOut) return;
    setLoggingOut(true);
    await logout({ redirectTo: "/login?logout=1" });
  }

  function handleMobileNavigate(to) {
    setMobileOpen(false);
    nav(to);
  }

  function handleOpenNotification(item) {
    if (!item) return;
    if (item.kind !== "messages") {
      const markIds = Array.isArray(item.memberIds) && item.memberIds.length
        ? item.memberIds
        : item.id;
      markPharmacyNotificationsRead(markIds);
    }
    setNotificationsOpen(false);
    setMobileOpen(false);
    nav(item.route || "/pharmacy/prescriptions");
  }

  function handleMarkAllNotificationsRead() {
    const ids = attentionNotificationItems
      .flatMap((item) =>
        Array.isArray(item?.memberIds) && item.memberIds.length
          ? item.memberIds
          : item?.id,
      )
      .filter(Boolean);
    if (!ids.length) return;
    markPharmacyNotificationsRead(ids);
  }

  function submitSearch(event) {
    event?.preventDefault?.();
    const targetPath = resolvePharmacySearchTarget(location.pathname);
    const params = new URLSearchParams();
    const nextQuery = String(searchValue || "").trim();
    if (nextQuery) params.set("q", nextQuery);
    nav(params.toString() ? `${targetPath}?${params.toString()}` : targetPath);
    setMobileOpen(false);
  }

  function clearSearch() {
    setSearchValue("");
    const targetPath = resolvePharmacySearchTarget(location.pathname);
    nav(targetPath);
  }

  async function handleSubmitFeedback(event) {
    event?.preventDefault?.();
    const note = String(feedbackNote || "").trim();
    if (!note || !userId || feedbackBusy) return;

    setFeedbackBusy(true);
    setFeedbackError("");
    try {
      await submitContextFeedback({
        userId,
        role: "pharmacy",
        route: `${location.pathname}${location.search || ""}${location.hash || ""}`,
        section,
        context: {
          entity: {
            type: "pharmacy-member",
            id: String(pharmacyMemberAccount?.id || userId || "").trim() || null,
            branchId:
              String(pharmacyMemberAccount?.branch_id || pharmacyProfile?.branchId || "").trim() ||
              null,
          },
          branchName:
            String(pharmacyProfile?.pharmacyName || pharmacyProfile?.tradingName || "").trim() ||
            String(pharmacyMemberAccount?.branch_name || "").trim(),
          theme,
          userEmail,
          device: {
            kind:
              window.innerWidth >= 1024 ? "desktop" : window.innerWidth >= 640 ? "tablet" : "mobile",
            platform: String(navigator.platform || ""),
            userAgent: String(navigator.userAgent || ""),
            language: String(navigator.language || ""),
            viewportWidth: Number(window.innerWidth || 0),
            viewportHeight: Number(window.innerHeight || 0),
            touchPoints: Number(navigator.maxTouchPoints || 0),
          },
        },
        message: note,
      });
      setFeedbackNote("");
      setFeedbackSent(true);
      window.setTimeout(() => {
        setFeedbackSent(false);
        setFeedbackOpen(false);
      }, 900);
    } catch (error) {
      setFeedbackError(error?.message || "We couldn’t save your feedback yet.");
    } finally {
      setFeedbackBusy(false);
    }
  }

  const avatarSrc =
    !avatarFailed && explicitPharmacyAvatarSrc ? explicitPharmacyAvatarSrc : FALLBACK_AVATAR;
  const attentionNotificationItems = React.useMemo(
    () =>
      groupAlertsForAttention(
        (Array.isArray(notifications) ? notifications : [])
          .map((item) => hydratePharmacyAlert(item))
          .filter((item) => item?.bellEligible && !item?.read)
      ),
    [notifications],
  );
  const unreadBellCount = attentionNotificationItems.reduce(
    (total, item) =>
      total + (
        Array.isArray(item?.memberIds) && item.memberIds.length
          ? item.memberIds.length
          : 1
      ),
    0,
  );

  return (
    <>
      <div className="sticky top-0 z-40 w-full border-b border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface))]">
        <div className="md:hidden">
          <div className="afrimed-rail afrimed-rail--comfortable [--rail-pad:12px] pt-4 pb-3 lg:[--rail-pad:var(--afrimed-rail-gutter-desktop)]">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                aria-label={t("pharmacy.header.openNavigation", "Open navigation")}
                title={t("pharmacy.header.openNavigation", "Open navigation")}
              >
                <HamburgerIcon />
              </button>

              <button
                type="button"
                onClick={() => nav("/pharmacy/dashboard")}
                aria-label={t("pharmacy.header.brand", "AfriMed Pharmacy")}
                title={t("pharmacy.header.brand", "AfriMed Pharmacy")}
                className="inline-flex min-w-0 flex-1 items-center"
              >
                <img src={BRAND_LOGO} alt={t("pharmacy.header.brandMark", "AfriMed")} className="h-7 w-auto" draggable="false" />
              </button>

              <button
                type="button"
                onClick={() => setNotificationsOpen(true)}
                className="relative grid h-10 w-10 shrink-0 place-items-center rounded-[12px] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                aria-label={t("pharmacy.header.notifications", "Notifications")}
                title={t("pharmacy.header.notifications", "Notifications")}
              >
                <img src={ICON_BELL} alt="" className="h-5 w-5" draggable="false" />
                {unreadBellCount ? (
                    <span className={`absolute right-1.5 top-1.5 min-w-[18px] rounded-full ${WELLNESS_BADGE_BG} px-1.5 py-[1px] text-[10px] font-semibold leading-4 text-white`}>
                      {Math.min(unreadBellCount, 9)}
                    </span>
                ) : null}
              </button>
            </div>

            <form className="mt-4" onSubmit={submitSearch} role="search" aria-label={t("pharmacy.common.search", "Search")}>
              <button type="submit" className="sr-only">
                {t("pharmacy.common.search", "Search")}
              </button>
              <AfrimedSearchBar
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                onClear={clearSearch}
                placeholder={t("pharmacy.header.searchPlaceholder", "Search stock, prescriptions, payments, messages")}
                ariaLabel={t("pharmacy.common.search", "Search")}
                className="bg-[rgba(var(--clr-on-surface-variant),0.08)] border-transparent"
                showTrailing={false}
              />
            </form>
          </div>
        </div>

        <div className="hidden md:block">
          <div className="afrimed-rail afrimed-rail--comfortable">
            <div className="flex h-14 items-center gap-4 lg:gap-6">
              <button
                type="button"
                onClick={() => nav("/pharmacy/dashboard")}
                aria-label={t("pharmacy.header.brand", "AfriMed Pharmacy")}
                title={t("pharmacy.header.brand", "AfriMed Pharmacy")}
                className="inline-flex items-center"
              >
                <img src={BRAND_LOGO} alt={t("pharmacy.header.brandMark", "AfriMed")} className="h-8 w-auto" draggable="false" />
              </button>

              <div className="flex min-w-0 flex-1 items-center gap-4">
                <div className="flex min-w-0 shrink-0 items-center gap-2 lg:gap-4">
                  {tabs.map((tab) => {
                    const active = section === tab.key || Boolean(tab.match?.(location.pathname));
                    return (
                      <NavLink key={tab.key} to={tab.to} className={() => clsChip(active)}>
                        <span className="inline-flex items-center gap-1.5">
                          <span>{tab.label}</span>
                          {Number(tab?.badgeCount || 0) > 0 ? (
                            <NavAlertBadge count={tab.badgeCount} className="shrink-0" />
                          ) : null}
                        </span>
                      </NavLink>
                    );
                  })}
                </div>

                <form
                  className="ml-auto hidden md:block w-full max-w-[260px] shrink-0"
                  onSubmit={submitSearch}
                  role="search"
                  aria-label={t("pharmacy.common.search", "Search")}
                >
                  <button type="submit" className="sr-only">
                    {t("pharmacy.common.search", "Search")}
                  </button>
                  <AfrimedSearchBar
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                    onClear={clearSearch}
                    placeholder={t("pharmacy.header.searchPlaceholder", "Search stock, prescriptions, payments, messages")}
                    ariaLabel={t("pharmacy.common.search", "Search")}
                    className="bg-[rgba(var(--clr-on-surface-variant),0.08)] border-transparent"
                    inputClassName="text-[13px] lg:text-label-medium"
                    showTrailing={false}
                  />
                </form>
              </div>

              <div ref={accountMenuRef} className="relative ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setNotificationsOpen((value) => !value)}
                  className="relative grid h-9 w-9 place-items-center rounded-xl hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                  aria-label={t("pharmacy.header.notifications", "Notifications")}
                  title={t("pharmacy.header.notifications", "Notifications")}
                >
                  <img src={ICON_BELL} alt="" className="h-5 w-5" draggable="false" />
                  {unreadBellCount ? (
                    <span className={`absolute right-0.5 top-0.5 min-w-[18px] rounded-full ${WELLNESS_BADGE_BG} px-1.5 py-[1px] text-[10px] font-semibold leading-4 text-white`}>
                      {Math.min(unreadBellCount, 9)}
                    </span>
                  ) : null}
                </button>

                <button
                  type="button"
                  onClick={() => setOpen((value) => !value)}
                  className="flex h-11 flex-col items-center justify-center rounded-[12px] px-1.5 hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                  aria-label={t("pharmacy.header.meMenu", "Me menu")}
                >
                  <div className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-[rgb(var(--clr-surface-cont-highest))]">
                    <img
                      src={avatarSrc}
                      alt=""
                      onError={() => setAvatarFailed(true)}
                      className={[
                        "h-full w-full",
                        avatarFailed ? "object-contain p-1" : "object-cover",
                      ].join(" ")}
                      draggable="false"
                    />
                  </div>

                  <div className="mt-0.5 flex items-center gap-1 text-label-small text-[rgb(var(--clr-on-surface-variant))]">
                    <span>{t("pharmacy.header.me", "Me")}</span>
                    <img
                      src={ICON_DROPDOWN}
                      alt=""
                      className={`h-3 w-3 transition ${open ? "rotate-180" : ""}`}
                      draggable="false"
                    />
                  </div>
                </button>

                {open ? (
                  <div className="absolute right-0 top-[calc(100%+8px)] z-[60] w-[300px] overflow-hidden rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.10),0px_6px_12px_-4px_rgba(26,27,35,0.08)]">
                    <MenuItem label={t("pharmacy.header.myAccount", "My account")} icon={AccountNavIcon} onClick={() => nav("/pharmacy/account")} />
                    <MenuItem label={t("pharmacy.nav.prescriptions", "Prescriptions")} icon={ICON_PRESCRIPTIONS} onClick={() => nav("/pharmacy/prescriptions")} />
                    <MenuItem label={t("pharmacy.nav.settings", "Settings")} icon={ICON_SETTINGS} onClick={() => nav("/pharmacy/settings")} />
                    <MenuItem
                      label={t("pharmacy.header.reportIssue", "Make suggestion")}
                      icon={FeedbackNavIcon}
                      onClick={() => {
                        setOpen(false);
                        setFeedbackOpen(true);
                      }}
                    />
                    <MenuItem label={t("pharmacy.nav.help", "Help")} icon={ICON_HELP} onClick={() => nav("/pharmacy/help")} />
                    <div className="border-y border-[rgba(var(--clr-outline-variant),0.55)] px-4 py-3">
                      <p className="text-body-medium text-[rgb(var(--clr-on-surface-variant))]">Theme</p>
                      <div className="mt-2 space-y-1">
                        {THEME_OPTIONS.map((option) => (
                          <ThemeOptionButton
                            key={option.value}
                            active={theme === option.value}
                            label={option.label}
                            icon={option.icon}
                            onClick={() => handleSelectTheme(option.value)}
                          />
                        ))}
                      </div>
                    </div>
                    <MenuItem
                      label={loggingOut ? t("pharmacy.header.signingOut", "Signing out…") : t("pharmacy.header.signOut", "Sign out")}
                      icon={ICON_SIGN_OUT}
                      onClick={() => setLogoutOpen(true)}
                    />
                  </div>
                ) : null}

                {notificationsOpen ? (
                  <div className="absolute right-[56px] top-[calc(100%+8px)] z-[60] hidden w-[320px] overflow-hidden rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.10),0px_6px_12px_-4px_rgba(26,27,35,0.08)] md:block">
                    <div className="border-b border-[rgba(var(--clr-outline-variant),0.55)] px-4 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-title-medium text-[rgb(var(--clr-on-surface))]">
                            {t("pharmacy.header.notifications", "Notifications")}
                          </p>
                          <p className="mt-1 text-body-small text-[rgb(var(--clr-on-surface-variant))]">
                            {t("pharmacy.header.notificationSubtitle", "Prescription routing and stock alerts for this branch.")}
                          </p>
                        </div>
                        {notifications.length ? (
                          <button
                            type="button"
                            onClick={handleMarkAllNotificationsRead}
                            className={`text-label-small ${WELLNESS_BADGE_TEXT}`}
                          >
                            {t("pharmacy.header.markAllRead", "Mark all read")}
                          </button>
                        ) : null}
                      </div>
                    </div>
                    {attentionNotificationItems.length ? (
                      <div className="max-h-[360px] overflow-y-auto">
                        {attentionNotificationItems.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => handleOpenNotification(item)}
                            className="flex w-full flex-col gap-1 border-b border-[rgba(var(--clr-outline-variant),0.4)] px-4 py-3 text-left hover:bg-[rgba(var(--clr-on-surface),0.04)] last:border-b-0"
                          >
                            <div className="flex items-start gap-2">
                              <span className={`mt-1 h-2.5 w-2.5 rounded-full ${WELLNESS_BADGE_BG} opacity-90`} />
                              <div className="min-w-0 flex-1">
                                <p className="text-body-medium text-[rgb(var(--clr-on-surface))]">{item.title}</p>
                                <p className="mt-1 text-body-small text-[rgb(var(--clr-on-surface-variant))]">{item.message}</p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="px-4 py-5 text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
                        {t("pharmacy.header.notificationsEmpty", "No routing or stock alerts yet.")}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {mobileOpen && typeof document !== "undefined"
        ? createPortal(
            <div className="md:hidden">
              <div
                className="fixed inset-0 z-[70] bg-[rgba(15,23,42,0.42)] backdrop-blur-[6px]"
                onClick={() => setMobileOpen(false)}
              />

              <aside
                className="fixed inset-y-0 left-0 z-[71] flex w-[min(88vw,336px)] flex-col bg-[rgb(var(--clr-surface))] shadow-[0px_24px_48px_-20px_rgba(26,27,35,0.22)]"
                aria-label={t("pharmacy.header.mobileNavigation", "Pharmacy navigation")}
              >
                <div className="flex items-center justify-between px-4 pt-4 pb-3">
                  <button
                    type="button"
                    onClick={() => handleMobileNavigate("/pharmacy/dashboard")}
                    aria-label={t("pharmacy.header.brand", "AfriMed Pharmacy")}
                    className="inline-flex items-center"
                  >
                    <img src={BRAND_LOGO} alt={t("pharmacy.header.brandMark", "AfriMed")} className="h-7 w-auto" draggable="false" />
                  </button>

                  <button
                    type="button"
                    onClick={() => setMobileOpen(false)}
                    className="grid h-10 w-10 place-items-center rounded-[12px] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                    aria-label={t("pharmacy.header.closeNavigation", "Close navigation")}
                  >
                    <img src={ICON_CLOSE} alt="" className="h-4 w-4" draggable="false" />
                  </button>
                </div>

                <div className="border-y border-[rgba(var(--clr-outline-variant),0.4)] px-4 py-3">
                  <form onSubmit={submitSearch} role="search" aria-label={t("pharmacy.common.search", "Search")}>
                    <button type="submit" className="sr-only">
                      {t("pharmacy.common.search", "Search")}
                    </button>
                    <AfrimedSearchBar
                      value={searchValue}
                      onChange={(event) => setSearchValue(event.target.value)}
                      onClear={clearSearch}
                      placeholder={t("pharmacy.header.searchPlaceholder", "Search stock, prescriptions, payments, messages")}
                      ariaLabel={t("pharmacy.common.search", "Search")}
                      className="bg-[rgba(var(--clr-on-surface-variant),0.08)] border-transparent"
                      showTrailing={false}
                    />
                  </form>
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-5">
                  <div className="mb-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-title-small text-[rgb(var(--clr-on-surface))]">
                          {t("pharmacy.header.notifications", "Notifications")}
                        </p>
                        <p className="mt-1 text-body-small text-[rgb(var(--clr-on-surface-variant))]">
                          {t("pharmacy.header.notificationSubtitle", "Prescription routing and stock alerts for this branch.")}
                        </p>
                      </div>
                      {unreadBellCount ? (
                        <span className={`rounded-full ${WELLNESS_BADGE_SOFT} px-2 py-1 text-label-small ${WELLNESS_BADGE_TEXT}`}>
                          {unreadBellCount}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-4 divide-y divide-[rgba(var(--clr-outline-variant),0.4)]">
                      {attentionNotificationItems.slice(0, 3).map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleOpenNotification(item)}
                          className="block w-full px-1 py-3 text-left"
                        >
                          <p className="text-body-medium text-[rgb(var(--clr-on-surface))]">{item.title}</p>
                          <p className="mt-1 text-body-small text-[rgb(var(--clr-on-surface-variant))]">{item.message}</p>
                        </button>
                      ))}
                      {!attentionNotificationItems.length ? (
                        <p className="py-3 text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
                          {t("pharmacy.header.notificationsEmpty", "No routing or stock alerts yet.")}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="space-y-1">
                    {mobileNavItems.map((item) => (
                      <MobileNavButton
                        key={item.key}
                        label={item.label}
                        icon={item.icon}
                        active={section === item.key || Boolean(item.match?.(location.pathname))}
                        badgeCount={item.badgeCount}
                        onClick={() => handleMobileNavigate(item.to)}
                      />
                    ))}
                  </div>
                </div>

                <div className="border-t border-[rgba(var(--clr-outline-variant),0.4)] px-4 py-4">
                  <div className="mb-4 flex items-center gap-3 border-b border-[rgba(var(--clr-outline-variant),0.4)] pb-4">
                    <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-[rgb(var(--clr-surface-cont-highest))]">
                      <img
                        src={avatarSrc}
                        alt=""
                        onError={() => setAvatarFailed(true)}
                        className={[
                          "h-full w-full",
                          avatarFailed ? "object-contain p-1" : "object-cover",
                        ].join(" ")}
                        draggable="false"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-body-medium text-[rgb(var(--clr-on-surface))]">
                        {t("pharmacy.header.me", "Me")}
                      </p>
                      <p className="text-body-small text-[rgb(var(--clr-on-surface-variant))]">
                        {t("pharmacy.header.pharmacyRole", "Pharmacy workspace")}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleMobileNavigate("/pharmacy/account")}
                    className="mb-1 flex w-full items-center gap-3 rounded-[12px] px-4 py-3 text-left text-body-large text-[rgb(var(--clr-on-surface))] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                  >
                    <AccountNavIcon />
                    <span>{t("pharmacy.header.myAccount", "My account")}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setMobileOpen(false);
                      setFeedbackOpen(true);
                    }}
                    className="mb-1 flex w-full items-center gap-3 rounded-[12px] px-4 py-3 text-left text-body-large text-[rgb(var(--clr-on-surface))] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                  >
                    <FeedbackNavIcon />
                    <span>{t("pharmacy.header.reportIssue", "Make suggestion")}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setLogoutOpen(true)}
                    className="flex w-full items-center gap-3 rounded-[12px] px-4 py-3 text-left text-body-large text-[rgb(var(--clr-on-surface))] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                  >
                    <img src={ICON_SIGN_OUT} alt="" className="h-5 w-5" draggable="false" />
                    <span>{loggingOut ? t("pharmacy.header.signingOut", "Signing out…") : t("pharmacy.header.signOut", "Sign out")}</span>
                  </button>
                </div>
              </aside>
            </div>,
            document.body,
          )
        : null}

      {notificationsOpen && typeof document !== "undefined"
        ? createPortal(
            <div className="md:hidden">
              <div
                className="fixed inset-0 z-[88] bg-[rgba(15,23,42,0.42)] backdrop-blur-[6px]"
                onClick={() => setNotificationsOpen(false)}
              />
              <div className="fixed inset-x-0 bottom-0 z-[89] rounded-t-[24px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0px_-12px_24px_-10px_rgba(29,79,215,0.10),0px_-6px_12px_-4px_rgba(26,27,35,0.08)]">
                <div className="border-b border-[rgba(var(--clr-outline-variant),0.4)] px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-title-medium text-[rgb(var(--clr-on-surface))]">
                        {t("pharmacy.header.notifications", "Notifications")}
                      </p>
                      <p className="mt-1 text-body-small text-[rgb(var(--clr-on-surface-variant))]">
                        {t("pharmacy.header.notificationSubtitle", "Prescription routing and stock alerts for this branch.")}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setNotificationsOpen(false)}
                      className="grid h-10 w-10 place-items-center rounded-[12px] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                      aria-label={t("pharmacy.header.closeNotifications", "Close notifications")}
                    >
                      <img src={ICON_CLOSE} alt="" className="h-4 w-4" draggable="false" />
                    </button>
                  </div>
                  {attentionNotificationItems.length ? (
                    <button
                      type="button"
                      onClick={handleMarkAllNotificationsRead}
                      className={`mt-3 text-label-small ${WELLNESS_BADGE_TEXT}`}
                    >
                      {t("pharmacy.header.markAllRead", "Mark all read")}
                    </button>
                  ) : null}
                </div>
                <div className="max-h-[60vh] overflow-y-auto px-5 py-5">
                  {attentionNotificationItems.length ? (
                    <div className="divide-y divide-[rgba(var(--clr-outline-variant),0.4)]">
                      {attentionNotificationItems.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleOpenNotification(item)}
                          className="block w-full py-3 text-left"
                        >
                          <p className="text-body-medium text-[rgb(var(--clr-on-surface))]">{item.title}</p>
                          <p className="mt-1 text-body-small text-[rgb(var(--clr-on-surface-variant))]">{item.message}</p>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
                      {t("pharmacy.header.notificationsEmpty", "No routing or stock alerts yet.")}
                    </p>
                  )}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      <AfriMedDialog
        open={feedbackOpen}
        onClose={() => (feedbackBusy ? null : setFeedbackOpen(false))}
        title={t("pharmacy.header.feedback.title", "Make suggestion")}
        subtitle={t(
          "pharmacy.header.feedback.subtitle",
          "Capture the current pharmacy context so AfriMed can reopen this exact lane later.",
        )}
        footer={
          <div className="flex items-center justify-end gap-3 px-5 py-4">
            <button
              type="button"
              className="rounded-xl border px-4 py-2.5 text-label-large text-[rgb(var(--clr-on-surface))]"
              style={{ borderColor: "rgba(24,50,119,0.18)" }}
              onClick={() => (feedbackBusy ? null : setFeedbackOpen(false))}
            >
              {t("common.cancel", "Cancel")}
            </button>
            <button
              type="button"
              className="rounded-xl px-4 py-2.5 text-label-large text-[rgb(var(--afrimed-on-primary))]"
              style={{ background: "rgb(var(--afrimed-primary))" }}
              onClick={handleSubmitFeedback}
              disabled={feedbackBusy || !String(feedbackNote || "").trim()}
            >
              {feedbackBusy
                ? t("pharmacy.header.feedback.sending", "Sending…")
                : t("pharmacy.header.feedback.send", "Send suggestion")}
            </button>
          </div>
        }
      >
        <form className="px-5 pb-2" onSubmit={handleSubmitFeedback}>
          <div className="rounded-[12px] bg-[rgba(var(--clr-on-surface),0.04)] px-4 py-3">
            <div className="text-label-medium text-[rgb(var(--clr-on-surface-variant))]">
              {t("pharmacy.header.feedback.context", "Current context")}
            </div>
            <div className="mt-1 text-body-medium text-[rgb(var(--clr-on-surface))]">
              {`${location.pathname}${location.search || ""}${location.hash || ""}`}
            </div>
          </div>

          <div className="mt-4">
            <label
              htmlFor="pharmacy-feedback-note"
              className="block text-label-medium text-[rgb(var(--clr-on-surface-variant))]"
            >
              {t("pharmacy.header.feedback.note", "What should AfriMed improve here?")}
            </label>
            <textarea
              id="pharmacy-feedback-note"
              value={feedbackNote}
              onChange={(event) => setFeedbackNote(event.target.value)}
              rows={5}
              placeholder={t(
                "pharmacy.header.feedback.placeholder",
                "Write a short note about what would make this workflow clearer or easier.",
              )}
              className="mt-2 w-full rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-3 text-body-medium text-[rgb(var(--clr-on-surface))] outline-none placeholder:text-[rgb(var(--clr-on-surface-variant))]"
            />
          </div>

          {feedbackError ? (
            <div className="mt-3 rounded-[12px] bg-[rgba(var(--clr-error),0.10)] px-4 py-3 text-body-medium text-[rgb(var(--clr-error))]">
              {feedbackError}
            </div>
          ) : null}

          {feedbackSent ? (
            <div className="mt-3 rounded-[12px] bg-[rgba(var(--afrimed-primary),0.08)] px-4 py-3 text-body-medium text-[rgb(var(--afrimed-primary))]">
              {t("pharmacy.header.feedback.sent", "Report saved with the current pharmacy context.")}
            </div>
          ) : null}
        </form>
      </AfriMedDialog>

      {logoutOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[220] flex items-end justify-center bg-[rgba(15,23,42,0.35)] px-3 pb-3 backdrop-blur-sm md:items-center md:px-0 md:pb-0"
            role="dialog"
            aria-modal="true"
            onClick={() => (loggingOut ? null : setLogoutOpen(false))}
          >
            <div
              className="w-full max-w-[420px] overflow-hidden rounded-[24px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_18px_45px_rgba(15,23,42,0.18)] md:rounded-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 md:p-6">
                <div className="mx-auto w-full max-w-[320px] flex items-start justify-center gap-3">
                  <img src={ICON_LOG_OUT_CRITICAL} alt="" className="h-6 w-6 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-title-medium text-on-surface">
                      Are you sure you want to log out?
                    </div>
                    <div className="mt-1 text-body-medium text-on-surface-var">
                      You’ll need to sign in again to continue.
                    </div>
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-center gap-3">
                  <button
                    type="button"
                    className="flex-1 rounded-xl px-4 py-3 text-label-large disabled:opacity-60"
                    style={{
                      background: "var(--State-Layers-Primary-Opacity-08,rgba(29,79,215,0.08))",
                      color: "var(--Schemes-Primary,#1D4FD7)",
                    }}
                    disabled={loggingOut}
                    onClick={() => setLogoutOpen(false)}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="flex-1 rounded-xl px-4 py-3 text-label-large disabled:opacity-60"
                    style={{
                      background: "var(--State-Layers-Critical-Opacity-08,rgba(248,83,58,0.08))",
                      color: "var(--Schemes-Critical,#F8533A)",
                    }}
                    disabled={loggingOut}
                    onClick={handleLogout}
                  >
                    {loggingOut ? "Logging out…" : "Log out"}
                  </button>
                </div>
              </div>

              <div className="h-px bg-[rgba(15,23,42,0.06)]" />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
